import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Form } from 'react-bootstrap';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import { LineChart } from 'echarts/charts';
import {
  GridComponent,
  LegendComponent,
  TitleComponent,
  TooltipComponent
} from 'echarts/components';
import * as echarts from 'echarts/core';
import { getColor } from 'helpers/utils';
import useToken from 'hooks/useToken';
import { useToast } from 'context/providers/ToastProvider';

echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  LineChart,
  LegendComponent
]);

const ConversionLineChart = ({ height }) => {
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [data, setData] = useState([]);
    const [cargando, setCargando] = useState(true);
    const { token, setToken } = useToken();
    const { showErrorToast } = useToast();

    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = currentYear; year >= 2020; year--) {
        years.push(year);
    }

    const monthNames = [
        'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun',
        'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'
    ];

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('es-MX', {
            style: 'currency',
            currency: 'MXN',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(value);
    };

    const ObtenerCostoPorLead = async (anio) => {
        setCargando(true);
        try {
            const response = await fetch(`/api/marketing/ObtenerCostoPorLead?anio=${anio}`, {
                method: 'GET',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const result = await response.json();
                if (result.iResponseType === 1 && result.detalles) {
                    const dataset = JSON.parse(result.detalles);
                    const rows = dataset.table2 || [];
                    
                    // Crear array con 12 meses inicializados
                    const monthlyData = Array(12).fill(0).map((_, index) => ({
                        mes: index + 1,
                        nombre: monthNames[index],
                        costoPorLeadActual: 0,
                        costoPorLeadAnterior: 0
                    }));

                    // Llenar con datos reales
                    rows.forEach(row => {
                        if (row.month && row.month >= 1 && row.month <= 12) {
                            const monthIndex = row.month - 1;
                            if (row.year === anio) {
                                monthlyData[monthIndex].costoPorLeadActual = row.costo_por_lead || 0;
                            } else {
                                monthlyData[monthIndex].costoPorLeadAnterior = row.costo_por_lead || 0;
                            }
                        }
                    });

                    setData(monthlyData);
                } else {
                    showErrorToast(result.vMessage || 'Error al obtener datos');
                    setData([]);
                }
            } else {
                showErrorToast(response.status + ' ' + response.statusText);
                if (response.status === 401) {
                    setToken(null);
                    location.href = "";
                }
                setData([]);
            }
        } catch (error) {
            showErrorToast('Hubo un problema con la petición: ' + error.message);
            console.error('Error fetching cost per lead:', error);
            setData([]);
        } finally {
            setCargando(false);
        }
    };

    useEffect(() => {
        if (token) {
            ObtenerCostoPorLead(selectedYear);
        }
    }, [selectedYear, token]);

    const getOptions = () => ({
        tooltip: {
            trigger: 'axis',
            padding: [7, 10],
            backgroundColor: getColor('gray-100'),
            borderColor: getColor('gray-300'),
            textStyle: { color: getColor('dark') },
            borderWidth: 1,
            formatter: (params) => {
                let tooltipContent = `<span class="text-600 fw-bold">${params[0].name}</span><br/>`;
                params.forEach(param => {
                    tooltipContent += `<span class="text-600">${param.seriesName}:</span> <span class="text-dark fw-bold">${formatCurrency(param.value)}</span><br/>`;
                });
                return tooltipContent;
            }
        },
        legend: {
            data: [`Costo por Lead ${selectedYear}`, `Costo por Lead ${selectedYear - 1}`],
            textStyle: { color: getColor('gray-700') }
        },
        grid: { 
            top: '15%', 
            right: '5%', 
            bottom: '10%', 
            left: '12%' 
        },
        xAxis: {
            type: 'category',
            data: data.map(d => d.nombre),
            axisLine: {
                lineStyle: {
                    color: getColor('gray-300')
                }
            },
            axisLabel: {
                color: getColor('gray-600')
            }
        },
        yAxis: {
            type: 'value',
            axisLabel: {
                formatter: (value) => formatCurrency(value),
                color: getColor('gray-600')
            },
            splitLine: {
                lineStyle: {
                    color: getColor('gray-200'),
                    type: 'dashed'
                }
            }
        },
        series: [
            {
                name: `Costo por Lead ${selectedYear}`,
                type: 'line',
                data: data.map(d => d.costoPorLeadActual),
                smooth: true,
                lineStyle: { color: '#FF8042', width: 2 },
                itemStyle: {
                    color: '#FF8042'
                },
                symbol: 'circle',
                symbolSize: 6
            },
            {
                name: `Costo por Lead ${selectedYear - 1}`,
                type: 'line',
                data: data.map(d => d.costoPorLeadAnterior),
                smooth: true,
                lineStyle: { color: '#8884d8', width: 2 },
                itemStyle: {
                    color: '#8884d8'
                },
                symbol: 'circle',
                symbolSize: 6
            }
        ]
    });

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                <Form.Select 
                    size="sm" 
                    value={selectedYear} 
                    style={{ width: 120 }} 
                    onChange={(e) => setSelectedYear(Number(e.target.value))}
                >
                    {years.map(year => (
                        <option key={year} value={year}>{year}</option>
                    ))}
                </Form.Select>
            </div>
            {cargando ? (
                <div style={{ height: `${height}px`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <p className="text-muted">Cargando...</p>
                </div>
            ) : (
                <ReactEChartsCore 
                    echarts={echarts} 
                    option={getOptions()} 
                    style={{ height: `${height}px` }}
                />
            )}
        </div>
    );
};

ConversionLineChart.propTypes = {
    height: PropTypes.number
};

ConversionLineChart.defaultProps = {
    height: 300
};

export default ConversionLineChart;
